import { buildMainToolbar } from 'core/utils/mainToolbar';
import { ADMIN_ACCESS_LEVELS } from 'core/utils/value';
import { TourStepDef } from './types';

/**
 * Bump when the step list changes shape, so a stepIndex persisted against the
 * old list is discarded rather than resuming at the wrong step.
 *
 * v2 added the Fail to Close and Incidents steps.
 */
export const TOUR_STEPS_VERSION = 2;

/**
 * The main dashboard only, NOT /security-dashboard.
 *
 * The header nav is identical on both (SecurityDashboard builds it from the
 * same buildMainToolbar call), so the nav anchors would resolve there. But
 * `dashboard-create-request` points at the Create New Request button, which
 * only the main Dashboard has. /security-dashboard is open to security and
 * scorchsoft-admin; security get no steps at all, so the one role this affects
 * is a scorchsoft-admin who lands there, and they would sit through the 4s
 * anchor timeout and lose that step.
 *
 * Restricting to /dashboard means the tour simply stays dormant on the security
 * dashboard and picks up in full when they reach /dashboard, which is where
 * their own nav link sends them.
 */
const isDashboard = (pathname: string): boolean => pathname === '/dashboard';

const isSites = (pathname: string): boolean => pathname === '/sites';

// Deliberately excludes /site/:id/incidents, which is a different screen.
const isSiteDetails = (pathname: string): boolean => /^\/site\/[^/]+$/.test(pathname);

const isIncidents = (pathname: string): boolean => pathname === '/incidents';

/**
 * Extra filtering metadata, stripped before the steps reach the controller.
 */
type StepGate = {
    /** This step points AT that nav item, so it needs it in the toolbar. */
    navTourId?: string;
    /** Needs that nav item present without pointing at it (same visibility rule). */
    requiresTourId?: string;
    /** Gated by ADMIN_ACCESS_LEVELS on the page itself, with no nav item of its own. */
    requiresAdmin?: boolean;
};

/**
 * Every step in the tour, in order. Nav steps carry the `tourId` of the menu
 * item they point at so they can be filtered against the real toolbar below.
 */
const ALL_STEPS: Array<TourStepDef & StepGate> = [
    {
        id: 'nav-useful-contacts',
        navTourId: 'nav-useful-contacts',
        title: 'Useful Contacts',
        description: 'Key contacts are stored here, providing a single reference point when an issue requires escalation.',
        target: '[data-tour="nav-useful-contacts"]',
        onMissing: 'skip',
        matchesRoute: isDashboard,
    },
    {
        id: 'nav-sites',
        navTourId: 'nav-sites',
        title: 'Sites',
        description: 'All sites available to you are listed here. Select a site to view its details or to begin a booking.',
        target: '[data-tour="nav-sites"]',
        onMissing: 'skip',
        matchesRoute: isDashboard,
    },
    {
        id: 'nav-incidents',
        navTourId: 'nav-incidents',
        title: 'Incidents',
        description: 'Incidents are reported and tracked here, including diary entries and shift handover reports.',
        target: '[data-tour="nav-incidents"]',
        onMissing: 'skip',
        matchesRoute: isDashboard,
    },
    {
        id: 'dashboard-create-request',
        title: 'Create a request',
        description: 'Select this to raise a new request. You will be directed to the site list to choose the relevant site.',
        target: '[data-tour="dashboard-create-request"]',
        onMissing: 'skip',
        matchesRoute: isDashboard,
        nextRoute: '/sites',
    },
    {
        id: 'sites-ftc',
        // No nav item of its own: the buttons are gated by ADMIN_ACCESS_LEVELS
        // on the Sites page itself.
        requiresAdmin: true,
        title: 'Fail to Close',
        description: 'Fail to Close reports are raised here. Update Fail to Close allows further information to be added to an existing report.',
        target: '[data-tour="sites-ftc"]',
        onMissing: 'skip',
        matchesRoute: isSites,
    },
    {
        id: 'sites-first-row',
        title: 'Select a site',
        description: 'Select a site from the list. The options menu on each row allows you to book a request or view the site details.',
        target: 'tr.tour-first-site-row',
        // A user with no sites yet still gets the explanation, anchored to the table.
        fallbackTarget: '[data-tour="sites-all-table"]',
        onMissing: 'fallback',
        matchesRoute: isSites,
        nextRoute: 'sitesFirstSite',
    },
    {
        id: 'site-details-overview',
        title: 'Site details',
        description: 'Each site now has a dedicated details page containing its address, codes, opening times and contacts.',
        target: '[data-tour="site-info-header"]',
        onMissing: 'centre',
        matchesRoute: isSiteDetails,
        needsASite: true,
    },
    {
        id: 'site-book-access',
        title: 'Book from the site',
        description: 'Any request type may be booked directly from the site you are viewing.',
        target: '[data-tour="site-book-access"]',
        onMissing: 'centre',
        matchesRoute: isSiteDetails,
        needsASite: true,
        nextRoute: '/incidents',
    },
    {
        // Incidents is an admin-only nav item, so requiring it gates these to
        // admins without repeating the role rule.
        id: 'incidents-summary',
        requiresTourId: 'nav-incidents',
        title: 'Incident summary',
        description: 'New, active, critical and completed totals are displayed here. Select one to filter the list below.',
        target: '[data-tour="incidents-summary"]',
        onMissing: 'centre',
        matchesRoute: isIncidents,
    },
    {
        id: 'incidents-report',
        requiresTourId: 'nav-incidents',
        title: 'Report an incident',
        description: 'New incidents are logged here. Once created, you may add diary entries, attach images and record its resolution.',
        target: '[data-tour="incidents-report"]',
        onMissing: 'centre',
        matchesRoute: isIncidents,
    },
];

/**
 * The steps this role should actually see.
 *
 * Nav steps are filtered against `buildMainToolbar(accessLevel)` rather than
 * re-implementing the role rules: if a menu item is not in that user's toolbar,
 * its step is dropped. That makes "a step can never point at a hidden menu item"
 * structural — change the toolbar rules and the tour follows automatically.
 *
 * Consequences worth knowing:
 *  - security users have no Useful Contacts / Sites / Incidents items and cannot
 *    raise requests, so they get an EMPTY list and the tour never runs for them.
 *  - plain requesters keep the Sites step and the whole request journey, but lose
 *    the two admin-only nav steps.
 */
export const buildTourSteps = (accessLevel?: string): TourStepDef[] => {
    if (!accessLevel || accessLevel === 'none') return [];

    const toolbarTourIds = new Set(
        buildMainToolbar(accessLevel)
            .map((item) => item.tourId)
            .filter(Boolean) as string[],
    );

    const isAdmin = ADMIN_ACCESS_LEVELS.includes(accessLevel);
    // No Sites item means no site journey either. Those steps all end on a
    // screen this role cannot reach.
    const canReachSites = toolbarTourIds.has('nav-sites');

    return ALL_STEPS
        .filter(({ navTourId, requiresTourId, requiresAdmin }) => {
            if (requiresAdmin && !isAdmin) return false;

            const needed = navTourId ?? requiresTourId;
            if (needed) return toolbarTourIds.has(needed);

            // Everything else is part of the site journey.
            return canReachSites;
        })
        // Strip the gating markers. They exist only for the filter above, and
        // the controller has no use for them.
        .map(({
            navTourId: _navTourId, requiresTourId: _requiresTourId, requiresAdmin: _requiresAdmin, ...step
        }) => step);
};

export default buildTourSteps;
